import { inject, Injectable } from '@angular/core';
import { ActiveCartService, CartActions } from '@cart/data-access';
import { ReceiptTypeCodes } from '@checkout-v2/const';
import { CheckoutActions, CheckoutApiService, PaymentModesFacade } from '@checkout-v2/data-access';
import { Actions, createEffect, EffectNotification, ofType, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { PaymentActions } from '@payment/data-access';
import { PaymentService } from '@payment/feature';
import { CurrentProfileService } from '@user/feature';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { PickupService, ReceiptService, TimeSlotsService } from '../services';

@Injectable()
export class PaymentModesEffects implements OnRunEffects {
  private readonly actions$ = inject(Actions);
  private readonly timeSlotsService = inject(TimeSlotsService);
  private readonly receiptService = inject(ReceiptService);
  private readonly pickupService = inject(PickupService);
  private readonly checkoutApiService = inject(CheckoutApiService);
  private readonly activeCartService = inject(ActiveCartService);
  private readonly paymentModeFacade = inject(PaymentModesFacade);
  private readonly paymentService = inject(PaymentService);
  private readonly currentProfileService = inject(CurrentProfileService);

  // Загрузить способы оплат
  // Отправить запрос на бекенд
  loadPaymentModes$: Observable<CheckoutActions.LoadPaymentModeListSuccess | CheckoutActions.LoadPaymentModeListFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CheckoutActions.LOAD_PAYMENT_MODE_LIST),
        concatLatestFrom(() => this.activeCartService.getActiveCartId()),
        filter(([, cartId]) => !!cartId),
        switchMap(([, cartId]) =>
          this.checkoutApiService.loadPaymentModes(cartId).pipe(
            map((res) => new CheckoutActions.LoadPaymentModeListSuccess(res)),
            catchError(() => of(new CheckoutActions.LoadPaymentModeListFail())),
          ),
        ),
      ),
    );

  // Изменился адрес магазина самовывоза текущий профиль или выбранный таймслот
  // Загрузить список способов оплат
  loadPaymentModesOnContextChange$: Observable<CheckoutActions.LoadPaymentModeList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_TIME_SLOTS_SUCCESS, CheckoutActions.LOAD_USER_PICKUP_SHOP_LIST_SUCCESS),
      switchMap(() =>
        combineLatest({
          isLegalProfile: this.currentProfileService.getLegalEntityStatus(),
          activeReceiptType: this.receiptService.getActiveReceiptTypeId(),
          activeTimeSlot: this.timeSlotsService.getSelectedTimeSlot(),
          activePickupShop: this.pickupService.activeShop$,
        }).pipe(
          startWith(undefined),
          pairwise(),
          filter(([prevState, currentState]) => {
            if (
              !currentState ||
              currentState.isLegalProfile ||
              (currentState.activeReceiptType === 'delivery' && !currentState.activeTimeSlot) ||
              (currentState.activeReceiptType === 'pickup' && !currentState.activePickupShop)
            ) {
              return false;
            }

            const isActiveTimeSlotChanged =
              JSON.stringify(currentState.activeTimeSlot) !== JSON.stringify(prevState?.activeTimeSlot);
            const isLegalProfileChanged = currentState.isLegalProfile !== prevState?.isLegalProfile;
            const isActivePickupShopChanged =
              JSON.stringify(currentState.activePickupShop) !== JSON.stringify(prevState?.activePickupShop);

            return (
              (currentState.activeReceiptType === 'delivery' && isActiveTimeSlotChanged) ||
              (currentState.activeReceiptType === 'pickup' && isActivePickupShopChanged) ||
              isLegalProfileChanged
            );
          }),
          // перед запросом оплат необходимо дождаться кастомерконсаймента и пересчета корзинки
          switchMap(() =>
            this.actions$.pipe(
              ofType(CheckoutActions.UPDATE_CUSTOMER_CONSIGNMENT_SUCCESS),
              take(1),
              switchMap(() =>
                this.actions$.pipe(ofType<CartActions.LoadCartSuccess>(CartActions.LOAD_CART_SUCCESS), take(1)),
              ),
            ),
          ),
          concatLatestFrom(() => [
            this.receiptService.getActiveReceiptTypeId(),
            this.timeSlotsService.isValid(),
            this.pickupService.isValid(),
          ]),
          filter(([, activeReceiptType, isTimeSlotsValid, isPickupShopValid]) => {
            if (activeReceiptType === 'delivery') {
              return isTimeSlotsValid;
            }
            if (activeReceiptType === 'pickup') {
              return isPickupShopValid;
            }

            return false;
          }),
          map(() => new CheckoutActions.LoadPaymentModeList()),
        ),
      ),
    ),
  );

  // Загружен список способов оплат
  // Загрузить список банковских карт
  loadBankCardsAfterLoadPaymentModes$: Observable<PaymentActions.LoadPaymentList> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.LoadPaymentModeListSuccess>(CheckoutActions.LOAD_PAYMENT_MODE_LIST_SUCCESS),
      map((action) => action.payload),
      filter((paymentModes) => !!paymentModes.find((mode) => mode.code === 'sbrf' && !!mode.hasBindings)),
      map(() => new PaymentActions.LoadPaymentList()),
    ),
  );

  // Изменился таймслот, способ самовывоза или профиль (не выбраны или не валидны)
  // Сбросить список таймслотов
  resetPyamentModes$: Observable<CheckoutActions.LoadPaymentModeListResetState> = createEffect(() =>
    combineLatest({
      isLegalProfile: this.currentProfileService.getLegalEntityStatus(),
      activeReceiptType: this.receiptService.getActiveReceiptTypeId(),
      activeTimeSlot: this.timeSlotsService.getSelectedTimeSlot(),
      isTimeSlotsValid: this.timeSlotsService.isValid(),
      activePickupShop: this.pickupService.activeShop$,
      isPickupValid: this.pickupService.isValid(),
    }).pipe(
      distinctUntilChanged(
        (prev, current) =>
          prev.isLegalProfile === current.isLegalProfile &&
          prev.activeReceiptType === current.activeReceiptType &&
          JSON.stringify(prev.activeTimeSlot) === JSON.stringify(current.activeTimeSlot) &&
          prev.isTimeSlotsValid === current.isTimeSlotsValid &&
          JSON.stringify(prev.activePickupShop) === JSON.stringify(current.activePickupShop) &&
          prev.isPickupValid === current.isPickupValid,
      ),
      filter(
        ({ isLegalProfile, activeReceiptType, activeTimeSlot, isTimeSlotsValid, activePickupShop, isPickupValid }) => {
          if (isLegalProfile) {
            return true;
          }

          if (activeReceiptType === ReceiptTypeCodes.Delivery) {
            return !activeTimeSlot || !isTimeSlotsValid;
          }

          if (activeReceiptType === ReceiptTypeCodes.Pickup) {
            return !activePickupShop || !isPickupValid;
          }

          return false;
        },
      ),
      concatLatestFrom(() => [this.paymentModeFacade.getListState()]),
      filter(([, state]) => !!state.error || !!state.success),
      map(() => new CheckoutActions.LoadPaymentModeListResetState()),
    ),
  );

  // Сброшен список способов оплат
  // Сбросить список банковских карт
  resetBankCardsOnResetList$: Observable<PaymentActions.ResetLoadPaymentList> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_PAYMENT_MODE_LIST_RESET_STATE),
      concatLatestFrom(() => [this.paymentService.getPayments()]),
      filter(([, paymentList]) => paymentList.length > 0),
      map(() => new PaymentActions.ResetLoadPaymentList()),
    ),
  );

  // ---------------------------------------

  // Выбран способ оплаты
  // Отправить запрос на бекенд
  setActivePaymentMode$: Observable<
    CheckoutActions.SetActivePaymentModeSuccess | CheckoutActions.SetActivePaymentModeFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.SetActivePaymentMode>(CheckoutActions.SET_ACTIVE_PAYMENT_MODE),
      map((action) => action.payload.paymentMode),
      concatLatestFrom(() => this.activeCartService.getActiveCartId()),
      filter(([, cartId]) => !!cartId),
      switchMap(([paymentMode, cartId]) => {
        if (paymentMode) {
          return this.checkoutApiService.setPaymentMode(cartId, paymentMode.code).pipe(
            map(() => new CheckoutActions.SetActivePaymentModeSuccess(paymentMode)),
            catchError(() => of(new CheckoutActions.SetActivePaymentModeFail())),
          );
        }

        return this.checkoutApiService.resetPaymentMode(cartId).pipe(
          map(() => new CheckoutActions.SetActivePaymentModeSuccess(paymentMode)),
          catchError(() => of(new CheckoutActions.SetActivePaymentModeFail())),
        );
      }),
    ),
  );

  // Выбран способ оплаты
  // Привязать банковскую карту к выбранному способу оплаты
  bindCardToPaymentMode$: Observable<PaymentActions.BindPayment> = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckoutActions.SetActivePaymentMode>(CheckoutActions.SET_ACTIVE_PAYMENT_MODE),
      map((action) => action.payload.paymentMode?.bindingId),
      filter((bindingId): bindingId is string => !!bindingId),
      map((bindingId) => new PaymentActions.BindPayment(bindingId)),
    ),
  );

  // Сброшен список способов оплат
  // Сбросить выбранный способ оплаты, если был выбран
  resetSelectedPaymentModeOnResetList$: Observable<CheckoutActions.SetActivePaymentMode> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_PAYMENT_MODE_LIST_RESET_STATE),
      concatLatestFrom(() => [this.paymentModeFacade.getActivePaymentMode()]),
      filter(([, activePaymentMode]) => Boolean(activePaymentMode)),
      map(() => new CheckoutActions.SetActivePaymentMode({ paymentMode: undefined })),
    ),
  );

  // При установке способа оплаты или привязки карты произошла ошибка
  // Сбросить выбранный способ оплаты, если был выбран
  resetSelectedPaymentModeOnBindingError$: Observable<CheckoutActions.SetActivePaymentMode> = createEffect(() =>
    this.actions$.pipe(
      ofType(PaymentActions.BIND_PAYMENT_FAIL, CheckoutActions.SET_ACTIVE_PAYMENT_MODE_FAIL),
      concatLatestFrom(() => [this.paymentModeFacade.getActivePaymentMode()]),
      filter(([, activePaymentMode]) => Boolean(activePaymentMode)),
      map(() => new CheckoutActions.SetActivePaymentMode({ paymentMode: undefined })),
    ),
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>): Observable<EffectNotification> {
    return this.actions$.pipe(
      ofType(CheckoutActions.CHECKOUT_INIT),
      exhaustMap(() => resolvedEffects$.pipe(takeUntil(this.actions$.pipe(ofType(CheckoutActions.CHECKOUT_DESTROY))))),
    );
  }
}
